import { usePage } from "@inertiajs/react"
import type { CSSProperties, ReactNode } from "react"

import { ActingAsAgencyBanner } from "@/components/admin/acting-as-agency-banner"
import { ActingAsBanner } from "@/components/admin/acting-as-banner"
import { AnalyticsNoticeBanner } from "@/components/analytics-notice-banner"
import { useCurrentContext } from "@/hooks/use-current-context"
import { useElementHeight } from "@/hooks/use-element-height"
import { AdminShell } from "@/layouts/app/admin-shell"
import { AgencyShell } from "@/layouts/app/agency-shell"
import AppSidebarLayout from "@/layouts/app/app-sidebar-layout"
import { BrandShell } from "@/layouts/app/brand-shell"
import { CreatorShell } from "@/layouts/app/creator-shell"
import { cn } from "@/lib/utils"
import type { BreadcrumbItem } from "@/types"

interface AppLayoutProps {
  children: ReactNode
  breadcrumbs?: BreadcrumbItem[]
}

/**
 * AppLayout — picks the shell based on `currentUser.role`.
 *
 * Server-side authorization is the boundary; layout routing is UX polish, not
 * a security gate. If `currentUser` is null (mid-flow / pre-share-props page),
 * we fall back to the legacy `AppSidebarLayout` to avoid a blank screen.
 *
 * OKS-117 — zero-brand agency: AgencyShell renders as the normal chrome.
 * `<BrandSwitcher>` self-hides at `availableBrands.length ≤ 1` (OKS-13 R3),
 * and the dashboard dispatcher lands the admin on `/agency/brands` where the
 * existing OKS-13 F6 empty-state CTA ("+ New brand") is the natural next
 * action. No layout-level intercept here — the AgencyShell handles the
 * 0-brand case structurally. (The OKS-5 `<NoBrand>` intercept that
 * previously gated this path was removed in OKS-117 along with its page +
 * i18n keys.)
 *
 * OKS-312 — `<ActingAsBanner>` is mounted ABOVE every shell here so the
 * "Acting as {Brand}" strip is visible app-wide (PRD A3, UX Spec §2.2).
 * The banner is a no-op (`null`) when `useCurrentContext().actingAs` is
 * null, so non-acting traffic pays no DOM cost.
 *
 * OKS-346 — When an OKS Admin is acting-as a brand (`actingAs != null` on
 * an `admin`-role request), we render the `<BrandShell>` instead of the
 * `<AdminShell>`. That makes the brand sidebar (Settings, Recruiting,
 * Missions, …) reachable end-to-end under the act-as context — fixing
 * the OKS-302/312 sequel where the banner appeared but no menu pointed at
 * the brand surface. Server is authoritative: the `acting_as` shared prop
 * is reset on every Inertia visit, so the swap is atomic — banner +
 * brand-sidebar flip together in a single render (PRD R3, UX Spec §2).
 * `actingAs == null` (including malformed cases the resolver clears
 * server-side per OKS-312 R5) reverts to `<AdminShell>` — UX Spec §3
 * "safe default."
 *
 * OKS-683 — `<ActingAsAgencyBanner>` is mounted BESIDE `<ActingAsBanner>`
 * (both no-op to `null` when their context signal is absent). Unlike the
 * brand axis, act-as-agency does NOT swap the shell: agency screens don't
 * render until OKS-684, so an acting-as-agency admin keeps the
 * `<AdminShell>` + banner only (the honest "context + banner" flow). The
 * two contexts are mutually exclusive server-side, so only one banner
 * ever renders.
 */
export default function AppLayout({ children, breadcrumbs }: AppLayoutProps) {
  const { currentUser, actingAs } = useCurrentContext()
  // OKS-694 — measure the app-wide banner stack and publish its height as the
  // `--banner-stack-h` CSS var on the wrapper below. The viewport-fixed
  // sidebar rail (a deep descendant) inherits the var and offsets its top +
  // height so it starts BELOW the banners instead of painting over them. When
  // no banner is active the stack measures 0 → the var is `0px` → no offset,
  // geometry identical to pre-fix. See `use-element-height` + the unlayered
  // `[data-slot="sidebar-container"]` rule in `application.css`.
  const [bannerStackRef, bannerStackHeight] = useElementHeight<HTMLDivElement>()

  // OKS-723 / OKS-820 — the Messages routes (creator AND brand) are
  // fixed-viewport chat surfaces. To bound them WITHOUT a measured var (the
  // `--banner-stack-h` ResizeObserver settle race that rendered the composer
  // below the fold on CI-Linux, where the analytics banner wraps to 197px),
  // this wrapper becomes a `h-[100dvh]` flex column for those routes ONLY: the
  // banner stack is `shrink-0` (natural height, whatever it wraps to) and the
  // shell root is `flex-1 min-h-0`, so flexbox hands the shell exactly
  // `100dvh − banner` with ZERO calc/var/observer — deterministic + correct on
  // first paint.
  //
  // OKS-820 — the brand `Crew/Messages/Index` surface is the sidebar/inset
  // analog: the same `h-[100dvh] overflow-hidden` wrapper + `shrink-0` banner
  // stack bounds the brand console chain (`BrandShell` overrides its own
  // sidebar-wrapper / SidebarInset / content-region to `min-h-0 flex-1`).
  // Route-scoped so it never touches the body-scroll of the other creator pages
  // or the admin/brand/agency console pages that also mount this wrapper.
  const pageComponent = usePage().component
  const isMessagesViewport =
    pageComponent === "Creator/Messages/Index" ||
    pageComponent === "Crew/Messages/Index"

  let shell: ReactNode
  switch (currentUser?.role) {
    case "admin":
      // OKS-346 — admin-in-act-as gets the BrandShell so the brand sidebar
      // renders. Plain admin (no actingAs) keeps the AdminShell as before.
      shell = actingAs ? (
        <BrandShell breadcrumbs={breadcrumbs}>{children}</BrandShell>
      ) : (
        <AdminShell breadcrumbs={breadcrumbs}>{children}</AdminShell>
      )
      break
    case "creator":
      shell = <CreatorShell>{children}</CreatorShell>
      break
    case "agency":
      // OKS-13 F1 SPLIT: agency gets its own shell + sidebar (Home /
      // Brands / Team / Invitations). Brand role keeps the existing
      // `BrandShell` — divergence flagged at TRD time.
      shell = <AgencyShell breadcrumbs={breadcrumbs}>{children}</AgencyShell>
      break
    case "brand":
      shell = <BrandShell breadcrumbs={breadcrumbs}>{children}</BrandShell>
      break
    default:
      // No role yet (e.g. pages that mount AppLayout before shared props are
      // wired). Use the legacy sidebar layout — same chrome the starter ships.
      shell = (
        <AppSidebarLayout breadcrumbs={breadcrumbs}>
          {children}
        </AppSidebarLayout>
      )
  }

  return (
    // OKS-694 — the var lives on a wrapper enclosing BOTH the banner stack and
    // the shell so it inherits down to the fixed sidebar rail (CSS vars flow to
    // descendants, not siblings). A bare div sets no transform/filter/contain,
    // so it does NOT become a containing block for the `position: fixed`
    // sidebar — the rail stays viewport-fixed as before.
    <div
      // OKS-723 — Messages route: a `h-[100dvh]` flex column so the shell can
      // flex-fit `100dvh − banner` with no measured var (see `isMessagesViewport`
      // note above). `overflow-hidden` clips any transient spill; it does NOT
      // clip the creator shell's `position: fixed` TabBar/modals (a plain
      // overflow-hidden div is not a containing block for fixed descendants).
      // All other routes: unstyled wrapper, body-scroll — byte-identical to
      // pre-OKS-723.
      className={cn(
        isMessagesViewport && "flex h-[100dvh] flex-col overflow-hidden",
      )}
      style={{ "--banner-stack-h": `${bannerStackHeight}px` } as CSSProperties}
    >
      {/*
       * OKS-694 — the app-wide banner stack. Measured via `bannerStackRef`
       * (its height drives `--banner-stack-h`). Testid is LQE's stable rect
       * handle for the banner↔sidebar coexistence geometry specs.
       *
       * OKS-532 — app-wide product-analytics notice, mounted ONCE above the
       * role shell (beside <ActingAsBanner>) so it covers every authenticated
       * shell with a single instance. Self-hides (returns null) for anon
       * users and accounts that already dismissed it, so non-applicable
       * traffic pays no DOM cost. Placed as the first shell banner (UX Spec
       * §2/§6 reading order): analytics → acting-as → acting-as-agency.
       *
       * OKS-723 — `shrink-0` on the Messages route so the banner keeps its
       * natural (possibly wrapped, 197px-on-CI) height and the shell flex-fills
       * the rest — the whole point of the var-free bound.
       */}
      <div
        data-testid="app-banner-stack"
        ref={bannerStackRef}
        className={cn(isMessagesViewport && "shrink-0")}
      >
        <AnalyticsNoticeBanner />
        <ActingAsBanner />
        <ActingAsAgencyBanner />
      </div>
      {shell}
    </div>
  )
}
